import React from 'react';
import styled from 'styled-components';
import {
  FiTrendingUp, FiClock, FiPhone, FiCheckCircle, FiSlash, FiActivity,
} from 'react-icons/fi';
import {
  C, StatCard, StatLabel, StatValueRow, StatValue, StatDelta,
} from '../chrome';
import { formatPct, formatDelta, formatDuration } from '../utils';

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 1.2rem;
  margin-bottom: 2.4rem;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const IconBox = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.7rem;
  background: ${(p) => `${p.color}1a`};
  border: 1px solid ${(p) => `${p.color}33`};
  color: ${(p) => p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

/**
 * Pick a colour for the connection-rate card based on threshold.
 *  >= 80%  green   |  60–79% yellow   |  < 60% red
 */
const rateColor = (rate) => {
  if (rate >= 0.8) return C.green;
  if (rate >= 0.6) return C.yellow;
  return C.red;
};

const deltaColor = (deltaPp) => {
  if (deltaPp > 0) return C.green;
  if (deltaPp < 0) return C.red;
  return C.textMuted;
};

const Tile = ({ label, value, delta, accent, icon, valueColor }) => (
  <StatCard accent={accent}>
    <TopRow>
      <StatLabel>{label}</StatLabel>
      <IconBox color={accent}>{icon}</IconBox>
    </TopRow>
    <StatValueRow>
      <StatValue color={valueColor}>{value}</StatValue>
      {delta != null && (
        <StatDelta color={deltaColor(delta.signRef !== undefined ? delta.signRef : 0)}>
          {delta.text}
        </StatDelta>
      )}
    </StatValueRow>
  </StatCard>
);

const KpiStrip = ({ kpis = {} }) => {
  const rate = Number(kpis.connectionRate || 0);
  const deltaPp = Number(kpis.deltaPp || 0);

  return (
    <Strip>
      <Tile
        label="Connect rate today"
        value={formatPct(rate)}
        valueColor={rateColor(rate)}
        delta={{ text: formatDelta(deltaPp), signRef: deltaPp }}
        accent={rateColor(rate)}
        icon={<FiTrendingUp size={16} />}
      />
      <Tile
        label="Pending queue"
        value={kpis.pending || 0}
        valueColor={kpis.pending > 25 ? C.yellow : C.textPrimary}
        accent={C.yellow}
        icon={<FiClock size={16} />}
      />
      <Tile
        label="Dialing now"
        value={kpis.dialing || 0}
        accent={C.blue}
        icon={<FiActivity size={16} />}
      />
      <Tile
        label="Connected today"
        value={kpis.connectedToday || 0}
        valueColor={C.green}
        accent={C.green}
        icon={<FiCheckCircle size={16} />}
      />
      <Tile
        label="Exhausted today"
        value={kpis.exhaustedToday || 0}
        valueColor={kpis.exhaustedToday > 10 ? C.red : C.textPrimary}
        accent={C.red}
        icon={<FiSlash size={16} />}
      />
      <Tile
        label="Avg call duration"
        value={formatDuration(kpis.avgDurationSeconds)}
        accent={C.purple}
        icon={<FiPhone size={16} />}
      />
    </Strip>
  );
};

export default KpiStrip;
